import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Paperclip, Trash2, MessageSquarePlus, LogOut, Loader2, Compass } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaidNote } from "@/components/Paywall";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FileCard } from "@/components/FileCard";
import { Markdown } from "@/components/ChatMessage";
import { GroupGames } from "@/components/GroupGames";
import { StreakBadge, GroupLeaderboard, NoteQuizButton, HelpfulButton, RankBadge, useWeeklyBoard } from "@/components/GroupExtras";

function NoteForm({ classId, onPosted }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await http.post("/files", fd); setFiles((x) => [...x, data]); } catch (err) { toast.error(formatError(err)); }
  };
  const post = async () => {
    setBusy(true);
    try {
      await http.post(`/groups/${classId}/notes`, { title, content, file_ids: files.map((f) => f.file_id) });
      setTitle(""); setContent(""); setFiles([]);
      onPosted();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121725] p-5 space-y-3" data-testid="note-form">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Note title" className="bg-white/5 border-white/10" data-testid="note-title-input" />
      <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share notes, formulas, a study guide… (markdown supported)" rows={4} className="bg-white/5 border-white/10" data-testid="note-content-input" />
      {files.length > 0 && <div className="flex flex-wrap gap-2">{files.map((f) => <FileCard key={f.file_id} file={f} onRemove={() => setFiles((x) => x.filter((y) => y.file_id !== f.file_id))} />)}</div>}
      <div className="flex justify-between">
        <input ref={ref} type="file" hidden onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.docx" data-testid="note-file-input" />
        <button onClick={() => ref.current?.click()} className="text-sm text-neutral-400 hover:text-white flex items-center gap-1.5" data-testid="note-attach-btn"><Paperclip className="h-4 w-4" /> Attach</button>
        <button onClick={post} disabled={busy || !title.trim()} className="px-4 py-2 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-40" data-testid="note-post-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Share with group"}
        </button>
      </div>
    </div>
  );
}

function ListingCard({ c, onSaved }) {
  const [listed, setListed] = useState(c.listed);
  const [desc, setDesc] = useState(c.description || "");
  const [busy, setBusy] = useState(false);
  const save = async (next = listed) => {
    setBusy(true);
    try {
      const { data } = await http.put(`/groups/${c.class_id}/listing`, { listed: next, description: desc });
      setListed(data.listed);
      onSaved(data);
      toast.success(data.listed ? "Group listed in Discover" : "Group is unlisted");
    } catch (e) { toast.error(formatError(e)); setListed(c.listed); } finally { setBusy(false); }
  };
  const dirty = desc !== (c.description || "");
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121725] p-5 space-y-3" data-testid="group-listing-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium flex items-center gap-2"><Compass className="h-4 w-4 text-violet-300" /> List in Discover</p>
          <p className="text-xs text-neutral-500 mt-1" data-testid="group-listing-status">{listed ? "Anyone signed in can find and join this group." : "Only people with the code can join."}</p>
        </div>
        <Switch checked={listed} disabled={busy} onCheckedChange={(v) => { setListed(v); save(v); }} aria-label="List this group in Discover" data-testid="group-listing-switch" />
      </div>
      <Textarea value={desc} onChange={(e) => setDesc(e.target.value.slice(0, 200))} rows={2} placeholder="Short description, e.g. GCSE Biology revision, weekly quizzes"
        className="bg-white/5 border-white/10 text-sm" aria-label="Group description" data-testid="group-description-input" />
      {dirty && <button onClick={() => save()} disabled={busy} className="text-xs px-3 py-1.5 rounded-full bg-sky-400 text-white hover:bg-sky-500 disabled:opacity-50" data-testid="group-description-save">Save description</button>}
    </div>
  );
}

export default function ClassDetailPage() {
  const { classId } = useParams();
  const { user, ent } = useAuth();
  const paid = !!ent?.features.groups;
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("notes");
  const { rows: board, badges } = useWeeklyBoard(classId);
  const onHelpful = (id, d) => setC((x) => ({ ...x, notes: x.notes.map((n) => (n.note_id === id ? { ...n, ...d } : n)) }));
  const load = useCallback(() => http.get(`/groups/${classId}`).then((r) => setC(r.data)).catch(() => navigate("/app/groups")), [classId, navigate]);
  useEffect(() => { load(); }, [load]);

  const delNote = async (id) => { await http.delete(`/groups/${classId}/notes/${id}`); load(); };
  const leave = async () => {
    if (!window.confirm(c.members.length <= 1 ? "You're the last member. Leaving deletes this group. Continue?" : "Leave this study group?")) return;
    await http.post(`/groups/${classId}/leave`);
    navigate("/app/groups");
  };
  const ask = (n) => navigate("/app/new", { state: { prefill: { text: `Help me understand this study group note: "${n.title}"\n\n${n.content}`.trim(), files: n.files } } });

  if (!c) return <div className="p-10 text-neutral-500 text-sm">Loading…</div>;
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 grid lg:grid-cols-[1fr_280px] gap-6 sm:gap-8">
        <div className="space-y-6 min-w-0">
          <div>
            <p className="text-xs text-neutral-500">Study group</p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="class-title">{c.name}</h1>
            <div className="mt-3"><StreakBadge streak={c.streak} /></div>
          </div>
          <div className="flex gap-1 border-b border-white/10">
            {["notes", "games"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`group-tab-${t}`}
                className={`px-4 py-2 text-sm capitalize border-b-2 -mb-px transition-colors ${tab === t ? "border-sky-400 text-white" : "border-transparent text-neutral-500 hover:text-white"}`}>{t}</button>
            ))}
          </div>
          {tab === "games" && <GroupGames group={c} />}
          {tab === "notes" && (paid ? <NoteForm classId={classId} onPosted={load} />
            : <PaidNote text="You can read and use everything here. Sharing notes and materials is on paid plans." testid="note-form-locked" />)}
          {tab === "notes" && !c.notes.length && <p className="text-sm text-neutral-500">No notes shared yet.</p>}
          {tab === "notes" && c.notes.map((n) => (
            <div key={n.note_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 rise" data-testid={`class-note-${n.note_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-medium break-words">{n.title}</p>
                  <p className="text-xs text-neutral-500 flex items-center gap-1.5">{n.author_name}<RankBadge rank={badges[n.author_id]} testid={`note-author-badge-${n.note_id}`} /> · {new Date(n.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <HelpfulButton classId={classId} note={n} onChanged={onHelpful} />
                  <NoteQuizButton classId={classId} note={n} />
                  <button onClick={() => ask(n)} className="text-xs px-3 py-1.5 rounded-full border border-sky-300/30 text-sky-300 hover:bg-sky-500/10 flex items-center gap-1.5" data-testid={`note-ask-tutor-${n.note_id}`}><MessageSquarePlus className="h-3.5 w-3.5" /> Ask tutor</button>
                  {paid && n.author_id === user.user_id && (
                    <button onClick={() => delNote(n.note_id)} className="p-2 text-neutral-500 hover:text-red-400" data-testid={`note-delete-${n.note_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
              {n.content && <div className="mt-3"><Markdown>{n.content}</Markdown></div>}
              {n.files?.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{n.files.map((f) => <FileCard key={f.file_id} file={f} />)}</div>}
            </div>
          ))}
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#121725] p-5">
            <p className="text-xs text-neutral-500 mb-1">Join code</p>
            <button onClick={() => { navigator.clipboard?.writeText(c.join_code); toast.success("Code copied"); }} className="font-mono text-2xl tracking-[0.3em] text-sky-300 flex items-center gap-2" data-testid="class-join-code">
              {c.join_code} <Copy className="h-4 w-4 text-neutral-500" />
            </button>
          </div>
          {c.can_manage && <ListingCard c={c} onSaved={(d) => setC((x) => ({ ...x, listed: d.listed, description: d.description }))} />}
          <GroupLeaderboard rows={board} badges={badges} />
          <div className="rounded-2xl border border-white/10 bg-[#121725] p-5" data-testid="class-members">
            <p className="text-sm font-medium mb-3">{c.members.length} {c.members.length === 1 ? "member" : "members"}</p>
            <ul className="space-y-2">
              {c.members.map((m) => (
                <li key={m.user_id} className="flex items-center gap-2 text-sm">
                  <span className="h-6 w-6 rounded-full bg-white/10 text-[10px] flex items-center justify-center">{m.name?.[0]?.toUpperCase()}</span>
                  <span className="truncate flex-1">{m.name}</span>
                  <RankBadge rank={badges[m.user_id]} testid={`member-badge-${m.user_id}`} />
                </li>
              ))}
            </ul>
          </div>
          <button onClick={leave} className="w-full text-sm px-4 py-2 rounded-full border border-red-400/30 text-red-300 hover:bg-red-500/10 flex items-center justify-center gap-2" data-testid="class-leave-btn">
            <LogOut className="h-3.5 w-3.5" /> Leave group
          </button>
        </aside>
      </div>
    </div>
  );
}
